import React, { useCallback, useEffect, useState } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { CircularProgress, MenuItem, Select, TextField } from '@mui/material';
import statesData from 'app/assets/jsons/State.json';
import { GridColDef } from '@mui/x-data-grid';
import { getCitiesAndZipsByStates } from 'app/services/SharedServices/SharedServices';
import { handleGridCellChangeAndStopEdit } from './changeDetectionHelper';

enum CountryNames {
  USA = 'USA',
  CANADA = 'Canada',
}

const GridDropdownCell = React.memo(function GridDropdownCell(params: any) {
  const ref = useGridApiContext();
  const { id, value, field, row, colDef } = params;
  const [cityOptions, setCityOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (params?.field === 'city' && row?.state) {
      fetchCities(row?.country, row?.state);
    }
  }, [row?.state]);

  const fetchCities = async (country: string, state: string) => {
    const cityValues = await fetchCitiValues(country, state);
    setCityOptions(cityValues);
  };

  const fetchCitiValues = useCallback(
    async (country: string, state: string) => {
      if (country === CountryNames.USA && state) {
        setIsLoading(true);
        const result = await getCitiesAndZipsByStates({
          states: [state],
          cities: [],
        });
        let filteredCities = [];

        if (result?.length) {
          filteredCities = result[0]?.cities?.map(item => ({ id: item.id, value: item.name, label: item.name }));
          setIsLoading(false);
        }
        return filteredCities;
      } else {
        return [];
      }
    },
    [row?.country, row?.state],
  );

  const getNewStateValue = useCallback(
    (newValue: string) => {
      if (field === 'country' && newValue) {
        const countryId: number = newValue === CountryNames.USA ? 1 : newValue === CountryNames.CANADA ? 2 : 0;
        if (newValue === CountryNames.USA || newValue === CountryNames.CANADA) {
          const statesList = statesData?.states.filter(e => e.CountryID === countryId).map(e => e.Description);
          return statesList[0] || '';
        }
      }
      return '';
    },
    [field, row],
  );

  const handleOnSelectChange = useCallback(
    async (event: any) => {
      const newValue = event.target.value;
      
      // Use the helper function for change detection and grid updates
      await handleGridCellChangeAndStopEdit({
        id,
        field,
        newValue,
        oldValue: value,
        apiRef: ref,
      });

      if (field === 'country') {
        const newStateValue = getNewStateValue(newValue);
        await ref.current.setEditCellValue({ id: id, field: 'state', value: newStateValue }, event);
        await ref.current.setEditCellValue({ id: id, field: 'city', value: '' }, event);
      } else if (field === 'state') {
        await ref.current.setEditCellValue({ id: id, field: 'city', value: '' }, event);
      } else if (field === 'disciplineAbbr') {
        await ref.current.setEditCellValue({ id: id, field: 'specialtyAbbr', value: '' }, event);
      }
    },
    [id, field, row, value, getNewStateValue],
  );

  const handleOnTextChange = async event => {
    await ref.current.setEditCellValue({ id: id, field: field, value: event.target.value, debounceMs: 50 }, event);
    ref.current.stopCellEditMode({ id: id, field: field });
  };

  const getOptions = useCallback(() => {
    if (field === 'city') {
      return cityOptions;
    } else if (colDef?.valueOptions && typeof colDef?.valueOptions === 'function') {
      return colDef.valueOptions({ id, row, field });
    }

    return colDef?.valueOptions || [];
  }, [params, cityOptions]);

  return (
    <>
      {(field === 'state' && !(row?.country === CountryNames.USA || row?.country === CountryNames.CANADA)) ||
      (field === 'city' && row?.country !== CountryNames.USA) ? (
        <TextField
          variant={'outlined'}
          size={'small'}
          onChange={handleOnTextChange}
          value={value}
          disabled={row?.isTimeOff}
        />
      ) : (
        <>
          {isLoading && field === 'city' ? (
            <CircularProgress size={20} sx={{ margin: 'auto' }} />
          ) : (
            <Select
              {...params}
              disableUnderline
              labelId="select-filled-label"
              value={value}
              onChange={handleOnSelectChange}
              sx={{ width: '100%' }}
              disabled={row?.isTimeOff}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: '300px',
                  },
                },
              }}
            >
              {getOptions()?.map((x: any, index) => {
                return (
                  <MenuItem key={x.id ?? index} value={x.value ?? x}>
                    {x.label ?? x}
                  </MenuItem>
                );
              })}
            </Select>
          )}
        </>
      )}
    </>
  );
});

export const renderDropdownCell: GridColDef['renderCell'] = params => {
  return <GridDropdownCell {...params} />;
};
